import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { AlertCircle, Loader2 } from 'lucide-react'
import { PropostaPublicaConfetti } from '@/components/PropostaPublica/PropostaPublicaConfetti'
import { PropostaPublicaShell } from '@/components/PropostaPublica/PropostaPublicaShell'
import { PropostaPublicaStep1 } from '@/components/PropostaPublica/PropostaPublicaStep1'
import { PropostaPublicaStep2 } from '@/components/PropostaPublica/PropostaPublicaStep2'
import { PropostaPublicaStep3 } from '@/components/PropostaPublica/PropostaPublicaStep3'
import { PropostaPublicaRevisaoModal } from '@/components/PropostaPublica/PropostaPublicaRevisaoModal'
import { usePropostaPublica } from '@/hooks/usePropostaPublica'
import { getPropostaValidityInfo, type WizardStep } from '@/utils/propostaPublicaUtils'

function LoadingScreen() {
  return (
    <div className="proposta-publica-page min-h-screen flex items-center justify-center bg-[var(--bg-main)]">
      <div className="flex flex-col items-center gap-3">
        <Loader2 size={32} className="animate-spin text-[var(--brand-primary)]" />
        <p className="text-sm text-[var(--text-muted)]">Carregando proposta...</p>
      </div>
    </div>
  )
}

function ErrorScreen({ message }: { message: string }) {
  return (
    <div className="proposta-publica-page min-h-screen flex items-center justify-center bg-[var(--bg-main)] px-4">
      <div className="max-w-md w-full text-center space-y-4">
        <AlertCircle size={48} className="mx-auto text-[#e5484d]" />
        <h1 className="text-xl font-semibold text-[var(--text-primary)]">Proposta não encontrada</h1>
        <p className="text-sm text-[var(--text-muted)]">{message}</p>
      </div>
    </div>
  )
}

export default function PropostaPublica() {
  const { proposalId } = useParams<{ proposalId: string }>()
  const p = usePropostaPublica(proposalId)

  useEffect(() => {
    document.documentElement.classList.add('proposta-publica-route')
    return () => document.documentElement.classList.remove('proposta-publica-route')
  }, [])

  if (p.loading) return <LoadingScreen />
  if (p.erro || !p.proposta) return <ErrorScreen message={p.erro || 'Proposta indisponível.'} />

  const validity = getPropostaValidityInfo(p.proposta)

  if (p.proposta.status === 'expirada') {
    return <ErrorScreen message="Esta proposta não está mais disponível para aceitação." />
  }

  const showNav = !p.aceita && p.proposta.status !== 'recusada'

  return (
    <>
      {p.aceita && <PropostaPublicaConfetti />}
      <PropostaPublicaShell
        validity={validity}
        step={p.step}
        showNav={showNav}
        canGoNext={p.canGoNext}
        accepting={p.aceitando}
        onBack={() => p.setStep((prev) => (prev - 1) as WizardStep)}
        onNext={() => p.setStep((prev) => (prev + 1) as WizardStep)}
        onAccept={() => void p.handleAceitar()}
        footerExtra={
          p.aceita ? (
            <footer className="relative z-10 border-t border-[var(--border)] mt-auto bg-[var(--bg-card)]">
              <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 text-center">
                <p className="text-[11px] text-[var(--text-muted)]">
                  Proposta gerada pela Rocket Search · {new Date().getFullYear()}
                </p>
              </div>
            </footer>
          ) : undefined
        }
      >
        <AnimatePresence mode="wait">
          {p.step === 1 && (
            <PropostaPublicaStep1
              proposta={p.proposta}
              empresaNome={p.customer.empresa}
              canContratar={p.canGoNext}
              onContratar={() => p.setStep((prev) => (prev + 1) as WizardStep)}
            />
          )}
          {p.step === 2 && (
            <PropostaPublicaStep2
              customer={p.customer}
              onChange={p.patchCustomer}
              cadastroSave={p.cadastroSave}
              onSolicitarRevisao={() => p.setShowRevisao(true)}
            />
          )}
          {p.step === 3 && (
            <PropostaPublicaStep3
              proposta={p.proposta}
              customer={p.customer}
              aceita={p.aceita}
              aceitando={p.aceitando}
              onAceitar={p.handleAceitar}
              onSolicitarRevisao={() => p.setShowRevisao(true)}
            />
          )}
        </AnimatePresence>
      </PropostaPublicaShell>

      <PropostaPublicaRevisaoModal
        open={p.showRevisao}
        revisaoForm={p.revisaoForm}
        revisaoLoading={p.revisaoLoading}
        revisaoEnviada={p.revisaoEnviada}
        onClose={() => p.setShowRevisao(false)}
        onResetEnviada={() => p.setRevisaoEnviada(false)}
        onChange={(patch) => p.setRevisaoForm((prev) => ({ ...prev, ...patch }))}
        onSubmit={p.handleSolicitarRevisao}
      />
    </>
  )
}
